import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { isEmpty } from 'lodash';

import { Helmet } from 'react-helmet';
import { Button, Form, Modal } from 'antd';

import { useBreadcrumbs } from '@/contexts/BreadcrumbContextProvider';
import { queryClient } from '@/Root';

import {
  AddressModal,
  DefaultRouteTabs,
  SaveButton,
} from '@/components/_common';
import {
  useCreateInsurerAddress,
  useDeleteInsurerAddress,
  useInsurerAddresses,
  useUpdateInsurerAddress,
} from '@/core/domain/people/InsurerAddress/InsurersAddress.hooks';
import { useInsurer } from '@/core/domain/people/InsurerContact/InsurersContact.hooks';
import { InsurerAddress } from '@/core/domain/people/InsurerAddress/InsurersAddress.types';
import { PageProps } from '@/types/PageProps';
import { useInsurerFormContext } from '@/components/Insurers/InsurerFormProvider';
import { useRouteMatch } from 'react-router-dom';

import InsurerAddressesList from '@/components/Insurers/InsurerAddressesList';
import { AddressDefaultType } from '@/components/_common/AddressModal/types';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import InsurerFooter from '@/components/Insurers/InsurerFooter';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';

const InsurerAddressesPage = ({ route }: PageProps) => {
  const { disabled, isLoading } = useInsurerFormContext();
  const { setParamLabel } = useBreadcrumbs();
  const [selectedAddress, setSelectedAddress] = useState<InsurerAddress>();
  const [showAddressModal, setShowAddressModal] = useState(false);
  const intl = useIntl();
  const { insurerId } = useRouteMatch<{ insurerId: string }>().params;

  const [formSubmit] = Form.useForm();

  const { isFetching, data } = useInsurer(Number(insurerId));

  const createMutation = useCreateInsurerAddress(Number(insurerId));
  const updateMutation = useUpdateInsurerAddress(Number(insurerId));
  const removeMutation = useDeleteInsurerAddress(Number(insurerId));
  const insurerAdress = useInsurerAddresses(Number(insurerId));

  useEffect(() => {
    if (!isEmpty(data) && data) {
      setParamLabel(':insurerId', data.name);
    }
  }, [data, isFetching]);

  const handleSaveClick = async (values: AddressDefaultType) => {
    try {
      const obj = {
        ...values,
        delivery: !!values.delivery,
        main: !!values.main,
      };

      if (!selectedAddress) {
        await createMutation.mutateAsync(obj);
      } else {
        await updateMutation.mutateAsync(obj);
      }

      await queryClient.refetchQueries(['insurer', insurerId, 'addresses']);
    } catch {}
  };

  const handleCloseAddressModal = () => {
    setSelectedAddress(undefined);
    setShowAddressModal(false);
  };

  const handleShowEditAddressModal = (address: InsurerAddress) => {
    setSelectedAddress(address);
    setShowAddressModal(true);
  };

  const showConfirmRemoveAddress = (address: InsurerAddress) => {
    Modal.confirm({
      title: intl.formatMessage({
        id: 'insurers.addresses.remove.modal.title',
      }),
      content: intl.formatMessage({
        id: 'insurers.addresses.remove.modal.content',
      }),
      onOk: async () => {
        try {
          await removeMutation.mutateAsync(address.id);

          await queryClient.refetchQueries(['insurer', insurerId, 'addresses']);
        } catch {}
      },
      okText: intl.formatMessage({
        id: 'insurers.addresses.remove.modal.okText',
      }),
      okType: 'danger',
    });
  };

  return (
    <DefaultRouteTabs
      permissions={route.permissions}
      route={route.parent}
      baseKey={insurerId}
      basePath={`/app/registry/insurers/${insurerId}`}
      extra={
        <SpaceForButton>
          <Button
            block
            type="primary"
            onClick={() => setShowAddressModal(true)}
            loading={isFetching}
            disabled={
              (!!insurerAdress.data && !!insurerAdress.data.length) ||
              !hasPermissions([Authorities.REGISTER_INSURER_EDIT])
            }
          >
            <FormattedMessage id="commons.entity.add" />
          </Button>

          <SaveButton
            block
            type="primary"
            onClick={formSubmit.submit}
            loading={isLoading}
            disabled={
              disabled || !hasPermissions([Authorities.REGISTER_INSURER_EDIT])
            }
          >
            {intl.formatMessage({
              id: 'layout.menu.registry.insurers.saveText',
            })}
          </SaveButton>
        </SpaceForButton>
      }
    >
      <Helmet
        title={intl.formatMessage({
          id: route.name,
        })}
      />

      <Form form={formSubmit} name="insurerAdresses" />

      <InsurerAddressesList
        address={insurerAdress.data ? insurerAdress.data : []}
        isLoading={
          createMutation.isLoading ||
          updateMutation.isLoading ||
          removeMutation.isLoading ||
          insurerAdress.isLoading
        }
        onEdit={handleShowEditAddressModal}
        onRemove={showConfirmRemoveAddress}
      />

      <InsurerFooter disabled={disabled} />

      <AddressModal
        loading={false}
        visible={showAddressModal}
        onClose={handleCloseAddressModal}
        onOk={handleSaveClick}
        address={selectedAddress}
        confirmLoading={createMutation.isLoading || updateMutation.isLoading}
        removeDelivery={false}
        removeMain={false}
      />
    </DefaultRouteTabs>
  );
};

export default InsurerAddressesPage;
